import isPropValid from '@emotion/is-prop-valid';
import { GoogleOAuthProvider } from '@react-oauth/google';
import debounce from 'lodash.debounce';
import {
  type ErrorInfo,
  type FC,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import {
  type DefaultTheme,
  type ShouldForwardProp,
  StyleSheetManager,
  ThemeProvider,
} from 'styled-components';

import { AppLoader, Error as ErrorComponent, useZoomDisable } from '@cuemath/leap';
import { GOOGLE_CLIENT_ID, IS_DEV } from '@~/constants/app-config';
import getAppTheme from '@~/constants/theme';
import CourseSwitcherProvider from '@~/context/course-context';
import AuthTransitions from '@~/features/user/auth-transitions/auth-transitions';
import useSentryCaptureException from '@~/hooks/use-sentry-capture-exception';
import useTrackComponentMounted from '@~/hooks/use-track-component-mounted';
import { ALL_ROUTES } from '@~/router/config';

import '@~/utils/intersection-observer';

import { getStore } from './store';

const { store, persistor } = getStore();
const router = createBrowserRouter(ALL_ROUTES);

const debouncedGetTheme = debounce((cb: (theme: DefaultTheme) => void) => {
  const newTheme = getAppTheme();

  cb(newTheme);
}, 200);

const shouldForwardProp: ShouldForwardProp<'web'> = (propName, elementToBeRendered) => {
  if (typeof elementToBeRendered === 'string') {
    const isValid = isPropValid(propName);

    if (!isValid && IS_DEV) {
      // eslint-disable-next-line no-console
      console.log(
        `[styled-components] Invalid prop "${propName}" forwarded to <${elementToBeRendered}>`,
      );
    }

    return isValid;
  }

  return true;
};

const App: FC = () => {
  const sentryCaptureException = useSentryCaptureException();
  const [theme, setTheme] = useState(() => getAppTheme());

  const handleError = useCallback(
    (error: Error, info: ErrorInfo) => {
      sentryCaptureException(error, info as Record<string, unknown>);
    },
    [sentryCaptureException],
  );

  useZoomDisable();
  useTrackComponentMounted('App');

  useEffect(() => {
    document.getElementById('app-loader-wrapper')?.remove();
  }, []);

  useEffect(() => {
    const onResize = () => {
      debouncedGetTheme(setTheme); // calls setTheme with the debounced value
    };

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <StyleSheetManager enableVendorPrefixes shouldForwardProp={shouldForwardProp}>
        <ThemeProvider theme={theme}>
          <ErrorBoundary
            fallback={<ErrorComponent height="100dvh" />}
            onError={handleError}
          >
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <AuthTransitions />
                <CourseSwitcherProvider>
                  <Suspense fallback={<AppLoader height="100dvh" />}>
                    <RouterProvider router={router} />
                  </Suspense>
                </CourseSwitcherProvider>
              </PersistGate>
            </Provider>
          </ErrorBoundary>
        </ThemeProvider>
      </StyleSheetManager>
    </GoogleOAuthProvider>
  );
};

export default App;
